import {
  doc,
  getDoc,
  setDoc,
  collection,
  addDoc,
  onSnapshot,
  query,
  orderBy,
  updateDoc,
  serverTimestamp,
  runTransaction,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';

export const getConversationId = (uid1, uid2) => [uid1, uid2].sort().join('_');

export const getOrCreateConversation = async (currentUser, otherUser) => {
  const convId = getConversationId(currentUser.uid, otherUser.uid);
  const convRef = doc(db, 'conversations', convId);
  const convSnap = await getDoc(convRef);
  if (!convSnap.exists()) {
    await setDoc(convRef, {
      participants: [currentUser.uid, otherUser.uid],
      participantNicknames: {
        [currentUser.uid]: currentUser.nickname,
        [otherUser.uid]: otherUser.nickname,
      },
      participantPhotos: {
        [currentUser.uid]: currentUser.photoURL || '',
        [otherUser.uid]: otherUser.photoURL || '',
      },
      lastMessage: '',
      lastMessageAt: null,
      lastMessageSenderId: '',
      unreadCount: { [currentUser.uid]: 0, [otherUser.uid]: 0 },
    });
  }
  return convId;
};

export const sendChatMessage = async (convId, sender, text, recipientUid, recipientToken) => {
  const convRef = doc(db, 'conversations', convId);
  const messagesRef = collection(db, 'conversations', convId, 'messages');

  let shouldNotify = false;
  await runTransaction(db, async transaction => {
    const convSnap = await transaction.get(convRef);
    const currentUnread = convSnap.data()?.unreadCount?.[recipientUid] ?? 0;
    shouldNotify = currentUnread === 0;
    transaction.update(convRef, {
      lastMessage: text,
      lastMessageAt: serverTimestamp(),
      lastMessageSenderId: sender.uid,
      [`unreadCount.${recipientUid}`]: currentUnread + 1,
    });
  });

  await addDoc(messagesRef, {
    senderId: sender.uid,
    senderNickname: sender.nickname,
    text,
    createdAt: serverTimestamp(),
  });

  if (shouldNotify && recipientToken) {
    await addDoc(collection(db, 'chatNotifications'), {
      isNew: true,
      messageToken: recipientToken,
      type: 'chat',
      senderNickname: sender.nickname,
      text: text.length > 80 ? `${text.slice(0, 80)}…` : text,
      url: 'https://www.gamebooking.top/',
    });
  }
};

export const markConversationAsRead = async (convId, uid) => {
  await updateDoc(doc(db, 'conversations', convId), {
    [`unreadCount.${uid}`]: 0,
  });
};

export const listenToMessages = (convId, onMessages) => {
  const q = query(
    collection(db, 'conversations', convId, 'messages'),
    orderBy('createdAt', 'asc'),
  );
  return onSnapshot(
    q,
    snapshot => onMessages(snapshot.docs.map(d => ({ id: d.id, ...d.data() }))),
    err => { if (err.code !== 'permission-denied') console.error('[Chat] messages listener:', err.code); },
  );
};

export const listenToConversations = (uid, onConversations) => {
  const q = query(
    collection(db, 'conversations'),
    where('participants', 'array-contains', uid),
  );
  return onSnapshot(
    q,
    snapshot => {
      const convs = snapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const ta = a.lastMessageAt?.toMillis() ?? 0;
          const tb = b.lastMessageAt?.toMillis() ?? 0;
          return tb - ta;
        });
      onConversations(convs);
    },
    err => { if (err.code !== 'permission-denied') console.error('[Chat] conversations listener:', err.code); },
  );
};
